import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  User,
  Building,
  Mail,
  Phone,
  Send,
  Eye,
  CheckCircle,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  History,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  MessageCircle,
} from 'lucide-react';
import { formatBackendDate } from '../../lib/dateUtils';
import { deriveReferencePresentation } from '../../lib/referencePresentation';
import API_BASE from '../../utils/apiBase';
import AwaitingDaysBadge from './AwaitingDaysBadge';

const API = API_BASE;

const toneClasses = {
  success: {
    row: 'border-green-200 bg-white hover:bg-green-50/40',
    expanded: 'bg-green-50',
    icon: 'bg-green-100 text-green-700',
    badge: 'bg-green-100 text-green-700 border-green-200',
  },
  danger: {
    row: 'border-red-200 bg-white hover:bg-red-50/40',
    expanded: 'bg-red-50',
    icon: 'bg-red-100 text-red-700',
    badge: 'bg-red-100 text-red-700 border-red-200',
  },
  warning: {
    row: 'border-amber-200 bg-white hover:bg-amber-50/40',
    expanded: 'bg-amber-50',
    icon: 'bg-amber-100 text-amber-700',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  info: {
    row: 'border-blue-200 bg-white hover:bg-blue-50/40',
    expanded: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-700',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  ready: {
    row: 'border-teal-200 bg-white hover:bg-teal-50/40',
    expanded: 'bg-teal-50',
    icon: 'bg-teal-100 text-teal-700',
    badge: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  muted: {
    row: 'border-slate-200 bg-white hover:bg-slate-50',
    expanded: 'bg-slate-50',
    icon: 'bg-slate-100 text-slate-600',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
  },
};

function StatusIcon({ tone }) {
  if (tone === 'success') return <CheckCircle className="h-5 w-5" />;
  if (tone === 'danger' || tone === 'warning') return <AlertTriangle className="h-5 w-5" />;
  if (tone === 'info') return <Clock className="h-5 w-5" />;
  if (tone === 'ready') return <Send className="h-5 w-5" />;
  return <AlertCircle className="h-5 w-5" />;
}

function Detail({ label, children }) {
  if (!children) return null;
  return (
    <div className="min-w-0 rounded-lg bg-white/80 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 break-words text-sm text-slate-800">{children}</div>
    </div>
  );
}

export default function ReferenceRow({
  row,
  employeeId,
  onViewResponse,
  onViewHistory,
  onRefresh,
  isAuditor = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showReplacementPrompt, setShowReplacementPrompt] = useState(false);
  const [replacementReason, setReplacementReason] = useState('');
  const [isRequestingReplacement, setIsRequestingReplacement] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [sendOptionsForceResend, setSendOptionsForceResend] = useState(false);
  const { token } = useAuth();

  const {
    key,
    reference_num,
    declared_referee,
    request_sent_at,
    request_lifecycle,
    has_response,
    response_received_at,
    allowed_actions = [],
  } = row;

  const presentation = deriveReferencePresentation(row, { isAuditor });
  const styles = toneClasses[presentation.tone] || toneClasses.muted;

  const primaryAction = presentation.primaryAction;
  const secondaryActions = (presentation.secondaryActions || []).filter((action) => {
    if (action === 'history') return Boolean(onViewHistory);
    if (action === 'resend_request') return allowed_actions.includes('resend_request') && !isAuditor;
    if (action === 'request_replacement') return allowed_actions.includes('request_replacement') && !isAuditor;
    return false;
  });

  const handleSendRequest = async (channel = 'email', forceResend = false) => {
    if ((channel === 'email' || channel === 'both') && !declared_referee?.email) {
      toast.error('Add the referee email before sending by email.');
      return;
    }
    if ((channel === 'whatsapp' || channel === 'both') && !declared_referee?.phone) {
      toast.error('Add the referee phone number before sending by WhatsApp.');
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/send-reference-request`,
        null,
        {
          params: { reference_num, force_resend: forceResend, channel },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'duplicate') {
        toast.info(response.data.message);
      } else if (response.data.status === 'success') {
        toast.success(response.data.message || `Reference request sent to ${declared_referee.name || declared_referee.email}`);
        if (onRefresh) onRefresh();
      } else if (response.data.status === 'email_failed') {
        toast.warning(response.data.message || 'Request saved, but the email did not send. Try again in a moment.');
        if (onRefresh) onRefresh();
      } else if (response.data.status === 'partial_success') {
        toast.warning(response.data.message || 'The request was delivered through one channel, but another channel needs attention.');
        if (response.data.manual_whatsapp_url) {
          window.open(response.data.manual_whatsapp_url, '_blank', 'noopener,noreferrer');
        }
        if (onRefresh) onRefresh();
      } else if (response.data.status === 'whatsapp_manual_required') {
        toast.warning(response.data.message || 'Open WhatsApp to finish sending the reference request.');
        if (response.data.manual_whatsapp_url) {
          window.open(response.data.manual_whatsapp_url, '_blank', 'noopener,noreferrer');
        }
        if (onRefresh) onRefresh();
      } else if (response.data.status === 'delivery_failed') {
        toast.error(response.data.message || 'The reference request was created but no delivery channel succeeded.');
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.data.message || 'Could not send reference request');
      }
      setShowSendOptions(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reference request');
    } finally {
      setIsSending(false);
    }
  };

  const openReview = (event) => {
    event.stopPropagation();
    if (onViewResponse) onViewResponse(reference_num);
  };

  const openReplacementPrompt = (event) => {
    event.stopPropagation();
    setShowMore(false);
    setShowReplacementPrompt(true);
  };

  const closeReplacementPrompt = () => {
    setShowReplacementPrompt(false);
    setReplacementReason('');
  };

  const handleRequestReplacement = async (event) => {
    event.stopPropagation();
    if (!replacementReason.trim()) {
      toast.error('Add a reason before requesting another referee.');
      return;
    }

    setIsRequestingReplacement(true);
    try {
      await axios.post(
        `${API}/references/${employeeId}/${reference_num}/request-replacement`,
        { replacement_reason: replacementReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('The applicant will be asked for another referee.');
      closeReplacementPrompt();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not request another referee. Please try again.');
    } finally {
      setIsRequestingReplacement(false);
    }
  };

  const renderPrimaryAction = () => {
    if (isAuditor || !primaryAction) return null;

    if (primaryAction === 'send_request' && allowed_actions.includes('send_request') && (declared_referee?.email || declared_referee?.phone)) {
      return (
        <div className="relative">
          <Button
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setSendOptionsForceResend(false);
              setShowSendOptions((value) => !value);
            }}
            disabled={isSending}
            className="h-8 rounded-lg bg-primary text-xs text-white hover:bg-primary-hover"
            data-testid={`reference-primary-action-${key}`}
          >
            {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />}
            Send request
          </Button>
          {showSendOptions && (
            <div
              className="absolute right-0 z-30 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
              data-testid={`reference-send-options-${key}`}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100 disabled:opacity-50"
                disabled={!declared_referee?.email || isSending}
                onClick={() => handleSendRequest('email', sendOptionsForceResend)}
              >
                <Mail className="h-4 w-4" /> Send by email
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100 disabled:opacity-50"
                disabled={!declared_referee?.phone || isSending}
                onClick={() => handleSendRequest('whatsapp', sendOptionsForceResend)}
              >
                <MessageCircle className="h-4 w-4" /> Send by WhatsApp
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100 disabled:opacity-50"
                disabled={!declared_referee?.email || !declared_referee?.phone || isSending}
                onClick={() => handleSendRequest('both', sendOptionsForceResend)}
              >
                <Send className="h-4 w-4" /> Send by both
              </button>
              {!declared_referee?.phone && (
                <p className="px-3 py-2 text-xs text-amber-700">Add a referee phone number to enable WhatsApp.</p>
              )}
            </div>
          )}
        </div>
      );
    }

    if (primaryAction === 'review_reference' && has_response && onViewResponse) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={openReview}
          className="h-8 rounded-lg text-xs"
          data-testid={`reference-primary-action-${key}`}
        >
          <Eye className="mr-1 h-3.5 w-3.5" />
          Review reference
        </Button>
      );
    }

    if (primaryAction === 'request_replacement' && allowed_actions.includes('request_replacement')) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={openReplacementPrompt}
          className="h-8 rounded-lg border-red-200 text-xs text-red-700 hover:bg-red-50"
          data-testid={`reference-primary-action-${key}`}
        >
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          Request another referee
        </Button>
      );
    }

    return null;
  };

  const renderSecondaryAction = (action) => {
    if (action === 'resend_request') {
      return (
        <div key={action} className="border-b border-slate-100 pb-1 mb-1">
          <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Send again</p>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            disabled={!declared_referee?.email || isSending}
            onClick={(event) => { event.stopPropagation(); handleSendRequest('email', true); setShowMore(false); }}
          >
            <Mail className="h-4 w-4" /> Email
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            disabled={!declared_referee?.phone || isSending}
            onClick={(event) => { event.stopPropagation(); handleSendRequest('whatsapp', false); setShowMore(false); }}
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            disabled={!declared_referee?.email || !declared_referee?.phone || isSending}
            onClick={(event) => { event.stopPropagation(); handleSendRequest('both', true); setShowMore(false); }}
          >
            <Send className="h-4 w-4" /> Email + WhatsApp
          </button>
        </div>
      );
    }

    if (action === 'history') {
      return (
        <button
          key={action}
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
          onClick={(event) => {
            event.stopPropagation();
            onViewHistory(key, presentation.title);
            setShowMore(false);
          }}
        >
          <History className="h-4 w-4" />
          History
        </button>
      );
    }

    if (action === 'request_replacement') {
      return (
        <button
          key={action}
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
          onClick={openReplacementPrompt}
          data-testid={`reference-request-replacement-${key}`}
        >
          <RefreshCw className="h-4 w-4" />
          Request another referee
        </button>
      );
    }

    return null;
  };

  const referee = declared_referee || {};

  return (
    <div
      className={`rounded-lg border transition-all ${isExpanded ? styles.expanded : styles.row}`}
      data-testid={`reference-row-${key}`}
    >
      <div className="p-4" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${styles.icon}`}>
            <StatusIcon tone={presentation.tone} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="break-words font-medium text-text-primary">{presentation.title}</h4>
              <Badge
                className={`text-[10px] ${styles.badge}`}
                data-testid={`reference-status-${key}`}
              >
                {presentation.statusLabel}
              </Badge>
              {!presentation.statusLabel.includes('Verified') && !has_response && request_sent_at && (
                <AwaitingDaysBadge
                  sentAt={request_sent_at}
                  testId={`reference-awaiting-${reference_num || key}`}
                />
              )}
            </div>
            <p className="mt-1 break-words text-sm text-text-muted">{presentation.whatHappened}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            {renderPrimaryAction()}
            {secondaryActions.length > 0 && (
              <div className="relative">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 rounded-lg px-2"
                  onClick={(event) => { event.stopPropagation(); setShowMore((value) => !value); }}
                  data-testid={`reference-more-${key}`}
                  aria-label={`More actions for ${presentation.title}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
                {showMore && (
                  <div
                    className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
                    data-testid={`reference-secondary-actions-${key}`}
                  >
                    {secondaryActions.map(renderSecondaryAction)}
                  </div>
                )}
              </div>
            )}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`expand-${key}`}>
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {showReplacementPrompt && (
        <div
          className="border-t border-red-100 bg-white px-4 py-4"
          data-testid={`reference-replacement-prompt-${key}`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="max-w-xl rounded-xl border border-red-100 bg-red-50/60 p-4">
            <h5 className="text-sm font-semibold text-slate-900">Request another referee?</h5>
            <p className="mt-1 text-sm text-slate-600">
              The applicant will be asked to provide new referee details.
            </p>
            <label htmlFor={`replacement-reason-${key}`} className="mt-3 block text-sm font-medium text-slate-700">
              Reason
            </label>
            <textarea
              id={`replacement-reason-${key}`}
              value={replacementReason}
              onChange={(event) => setReplacementReason(event.target.value)}
              className="mt-1 min-h-[84px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid={`reference-replacement-reason-${key}`}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleRequestReplacement}
                disabled={isRequestingReplacement}
                className="rounded-lg bg-red-700 text-white hover:bg-red-800"
                data-testid={`reference-confirm-replacement-${key}`}
              >
                {isRequestingReplacement ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
                Request another referee
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={(event) => { event.stopPropagation(); closeReplacementPrompt(); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="border-t border-white/70 px-4 pb-4">
          <div className="grid gap-3 pt-4 sm:grid-cols-2">
            <Detail label="Status">{presentation.statusLabel}</Detail>
            <Detail label="Owner">{presentation.owner}</Detail>
            <Detail label="Referee">
              {referee.name || referee.email ? (
                <div className="space-y-1">
                  {referee.name && <p><User className="mr-1 inline h-3.5 w-3.5" />{referee.name}</p>}
                  {referee.organisation || referee.company ? <p><Building className="mr-1 inline h-3.5 w-3.5" />{referee.organisation || referee.company}</p> : null}
                  {referee.email && <p className="break-all"><Mail className="mr-1 inline h-3.5 w-3.5" />{referee.email}</p>}
                  {referee.phone && <p><Phone className="mr-1 inline h-3.5 w-3.5" />{referee.phone}</p>}
                </div>
              ) : 'Not added yet'}
            </Detail>
            <Detail label="What happened">{presentation.whatHappened}</Detail>
            <Detail label="Next">{presentation.nextStep}</Detail>
            {request_sent_at && (
              <Detail label="Request sent">{formatBackendDate(request_sent_at, { format: 'medium' })}</Detail>
            )}
            {response_received_at && (
              <Detail label="Reply received">{formatBackendDate(response_received_at, { format: 'medium' })}</Detail>
            )}
            {request_lifecycle?.is_stale && (
              <Detail label="Follow up">This request has been waiting for {request_lifecycle.stale_days} days.</Detail>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
